import { readStoredString } from './readStoredJson';
import { isProduction, isLocal } from './environment';
import type { AnalyticsEnv, IdentityProps } from '@shared/analytics/events';
import type { UserControls } from '@shared/me/wire';

/**
 * Client-side analytics helpers shared by useSegmentTrack (track events) and
 * usePageTracking (page events), so both stamp the same identity spine + env
 * and honor the same emission gate as the server catalog.
 */

const DEV_ENABLE_KEY = 'terra_analytics_dev';

/** Runtime env stamped on every client event. Non-prod, non-local host = staging. */
export function analyticsEnv(): AnalyticsEnv {
  if (isProduction()) return 'production';
  if (isLocal()) return 'development';
  return 'staging';
}

/**
 * Whether client analytics should emit. Production always emits. Non-production
 * is OFF by default so dev/staging never pollute the prod Segment browser
 * source — opt in for local QA (visible in the Segment Chrome extension) by
 * setting `localStorage['terra_analytics_dev'] = '1'`. Once a dedicated browser
 * dev source exists, this can key off that write key instead.
 */
export function analyticsEnabled(): boolean {
  if (isProduction()) return true;
  try {
    return readStoredString(window.localStorage, DEV_ENABLE_KEY) === '1';
  } catch {
    return false;
  }
}

/** The catalog identity spine for a client event — same snake_case shape the
 * server emits, so client + server events join on the same keys in Snowflake. */
export function analyticsIdentity(controls: UserControls): IdentityProps {
  return {
    rep_id: controls.userId ?? null,
    rep_email: controls.email ?? null,
    // Real team, not the lens: see "Analytics identity stays real" in the
    // role-view-switcher plan.
    user_type: controls.realUserType,
    flow_role: controls.realFlowRole,
    impersonator_id: controls.impersonation?.impersonatorId ?? null,
    env: analyticsEnv(),
  };
}
